// Shared helpers for the "fetch page 1 with count, then fan out the rest in
// parallel" pattern used across the dashboard/report services.

// Safety ceiling on paged fetch-alls: guards against an absurd/incorrect
// `pagination.total` from the backend turning into a huge or infinite loop.
export const MAX_FETCH_ALL_PAGES = 200

// Number of pages fetched concurrently per batch when fanning out the
// remaining pages of a fetch-all. Without this cap, a bogus/huge
// `pagination.total` could fire up to MAX_FETCH_ALL_PAGES - 1 simultaneous
// requests.
export const PAGED_FETCH_BATCH_SIZE = 10

// Computes how many pages a fetch-all should walk, given the reported total
// and page size. Floors at 1 page (covers a zero/negative total) and caps at
// MAX_FETCH_ALL_PAGES, warning via the caller-supplied `context` string when
// the cap actually truncates the walk so the silent-truncation case is at
// least visible in logs.
export const clampTotalPages = (total: number, pageSize: number, context: string): number => {
  const rawPages = Math.ceil(total / pageSize)
  const pages = rawPages < 1 ? 1 : rawPages

  if (pages > MAX_FETCH_ALL_PAGES) {
    console.warn(
      `[${context}] fetch-all total pages (${pages}) exceeds MAX_FETCH_ALL_PAGES (${MAX_FETCH_ALL_PAGES}); truncating to the first ${MAX_FETCH_ALL_PAGES} pages`
    )
    return MAX_FETCH_ALL_PAGES
  }

  return pages
}

// Fetches the given page numbers in sequential batches of
// PAGED_FETCH_BATCH_SIZE (parallel within a batch, sequential across
// batches), preserving the input order in the returned array.
export const fetchPagesInBatches = async <T>(
  pageNumbers: number[],
  fetchOne: (page: number) => Promise<T>
): Promise<T[]> => {
  const results: T[] = []

  for (let index = 0; index < pageNumbers.length; index += PAGED_FETCH_BATCH_SIZE) {
    const batch = pageNumbers.slice(index, index + PAGED_FETCH_BATCH_SIZE)
    const batchResults = await Promise.all(batch.map((page) => fetchOne(page)))
    results.push(...batchResults)
  }

  return results
}

export interface SequentialPage<T> {
  items: T[]
  /**
   * The endpoint's reported row count, when it reports one. The walk never
   * trusts it on its own — see `fetchAllPagesSequentially`.
   */
  total?: number
}

export interface SequentialFetchResult<T> {
  rows: T[]
  /**
   * True when the walk hit `maxPages` with a full page still in hand.
   *
   * Deliberately conservative at the boundary: data that ends exactly on the
   * cap is indistinguishable from data that runs past it without spending one
   * more request, so it is reported as truncated. Using `total` to tell the two
   * apart would mark a genuinely cut-short export complete whenever an endpoint
   * under-reports its count — the silent truncation this walk exists to prevent
   * — so an unnecessary "may be incomplete" note is the better error to make.
   */
  truncated: boolean
}

/**
 * Walks an endpoint page by page until it runs out of rows, for the "export
 * everything behind these filters, not just the page on screen" case.
 *
 * A full last page keeps the walk going even once the reported `total` is
 * reached: an under-reported total (a filtered count, a count taken before a
 * write) would otherwise truncate the file silently. `maxPages` is the backstop
 * for the opposite mistake — an over-reported or absent total — and stopping
 * there is reported as `truncated` so the caller can say so rather than hand
 * over a file that looks complete.
 *
 * Sequential by design: these walks are user-triggered one at a time, and the
 * page count isn't known up front, so there is nothing to fan out over.
 */
export const fetchAllPagesSequentially = async <T>(
  fetchPage: (page: number) => Promise<SequentialPage<T>>,
  { pageSize, maxPages }: { pageSize: number; maxPages: number }
): Promise<SequentialFetchResult<T>> => {
  const rows: T[] = []
  let page = 1
  let lastPageFull = true
  let total = Infinity

  while ((rows.length < total || lastPageFull) && page <= maxPages) {
    const { items, total: reportedTotal } = await fetchPage(page)
    total = reportedTotal ?? 0

    // An empty page is the end of the data, whatever the total claimed.
    if (!items.length) {
      lastPageFull = false
      break
    }

    rows.push(...items)
    lastPageFull = items.length === pageSize
    page += 1
  }

  return { rows, truncated: lastPageFull && page > maxPages }
}
